import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { styled } from "@mui/material/styles";

/**
 * Comparación por tipos de símbolos y estados de alineación.
 *
 * Réplica de graficar_comparacion_tipos(MasterDomain, IESDomain, final_states)
 * del notebook del autor: por cada tipo de símbolo, una barra del Master
 * (referencia total) junto a una barra apilada del IES dividida por estado.
 *
 * Recibe statistics.plot_symbol_type_comparison del backend:
 *   { master_counts, ies_counts_by_state, all_types }
 */

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "none",
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

// Master en naranja; IES en la progresión celeste -> azul del notebook.
const COLOR_MASTER = "#E69F00";
const COLORES_IES = {
  Omisión: "#87CEFA",
  "No similitud": "#87CEFA",
  Diferencia: "#1E90FF",
  Similitud: "#00008B",
};

// Orden de apilado fijo, igual que en el notebook (orden_estados).
const ORDEN_ESTADOS = ["Omisión", "Diferencia", "Similitud"];

// Texto visible de cada estado. La clave 'Omisión' es la que envía el backend.
const ETIQUETAS = {
  Similitud: "Similitud",
  Diferencia: "Diferencia",
  Omisión: "Omisión en Máster",
  "No similitud": "No similitud",
};

const MaterialComparisonChart = ({
  masterTypes = {},
  iesTypes = {},
  plotData = null,
  title = "",
}) => {
  const { typesArray, masterData, seriesIes } = React.useMemo(() => {
    let tipos = [];
    let master = [];
    let porEstado = {};

    if (plotData && plotData.master_counts && plotData.ies_counts_by_state) {
      const masterCounts = plotData.master_counts;
      const iesCounts = plotData.ies_counts_by_state;

      // El backend ya envía la unión de tipos (Master ∪ IES) en all_types.
      tipos =
        plotData.all_types && plotData.all_types.length
          ? plotData.all_types
          : Array.from(
              new Set([...Object.keys(masterCounts), ...Object.keys(iesCounts)])
            ).sort();

      master = tipos.map((t) => masterCounts[t] || 0);

      ORDEN_ESTADOS.forEach((estado) => {
        const valores = tipos.map((t) => {
          const fila = iesCounts[t] || {};
          // 'Omisión' y 'No similitud' son la misma categoría visual.
          if (estado === "Omisión") {
            return (fila["Omisión"] || 0) + (fila["No similitud"] || 0);
          }
          return fila[estado] || 0;
        });
        // Solo se grafican los estados con algún valor.
        if (valores.some((v) => v > 0)) porEstado[estado] = valores;
      });
    } else {
      // Sin plotData del backend no se puede desglosar por estado: se muestra
      // únicamente el conteo por tipo de cada dominio.
      tipos = Array.from(
        new Set([...Object.keys(masterTypes), ...Object.keys(iesTypes)])
      ).sort();
      master = tipos.map((t) => masterTypes[t] || 0);
      const totalIes = tipos.map((t) => iesTypes[t] || 0);
      if (totalIes.some((v) => v > 0)) porEstado = { IES: totalIes };
    }

    return { typesArray: tipos, masterData: master, seriesIes: porEstado };
  }, [plotData, masterTypes, iesTypes]);

  if (!typesArray.length) {
    return (
      <ChartContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Sin datos de tipos
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  const series = [
    {
      data: masterData,
      label: "Master",
      id: "masterSeries",
      color: COLOR_MASTER,
    },
    ...Object.entries(seriesIes).map(([estado, valores]) => ({
      data: valores,
      label: estado === "IES" ? "IES" : `IES - ${ETIQUETAS[estado] || estado}`,
      id: `ies-${estado}`,
      color: COLORES_IES[estado] || "#90a4ae",
      stack: "ies",
    })),
  ];

  return (
    <ChartContainer>
      {title ? (
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: "bold", fontSize: "1rem" }}
        >
          {title}
        </Typography>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <BarChart
          height={300}
          series={series}
          xAxis={[
            {
              data: typesArray,
              scaleType: "band",
              categoryGapRatio: 0.4,
              barGapRatio: 0.1,
              tickLabelStyle: { fontSize: 10 },
            },
          ]}
          yAxis={[
            {
              label: "Número de Términos",
              tickMinStep: 1,
              labelStyle: { fontSize: 11 },
            },
          ]}
          margin={{ left: 55, right: 16, top: 40, bottom: 45 }}
          slotProps={{
            legend: {
              direction: "row",
              position: { vertical: "top", horizontal: "middle" },
              itemMarkWidth: 10,
              itemMarkHeight: 10,
              labelStyle: { fontSize: 10 },
            },
          }}
        />
      </Box>
    </ChartContainer>
  );
};

export default MaterialComparisonChart;
